import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import PasswordResetRequest from "./PasswordResetRequest";

export default function AuthModal({ isOpen, onClose, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // 'login', 'register', or 'reset'
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    password2: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();

  const isLogin = mode === "login";
  const isReset = mode === "reset";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }

    if (formData.email && !formData.email.includes("@")) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    if (!isLogin && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!isLogin && formData.password !== formData.password2) {
      newErrors.password2 = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        // Success - close modal
        handleClose();
      } else {
        const result = await register({
          email: formData.email,
          password1: formData.password,
          password2: formData.password2,
        });
        
        // If email verification is required, show message but keep modal open
        if (result?.requiresVerification) {
          // Switch to login tab and clear form
          setFormData({
            email: "",
            password: "",
            password2: "",
          });
          setMode("login");
          // Don't close modal - user can see the message and login tab
        } else {
          // Auto-login succeeded or not needed - close modal
          handleClose();
        }
      }
    } catch (err) {
      // Extract errors from API response
      if (err.response?.data) {
        const apiErrors = err.response.data;
        const fieldErrors = {};

        // Map Django error fields to form fields
        if (apiErrors.email) {
          fieldErrors.email = Array.isArray(apiErrors.email)
            ? apiErrors.email[0]
            : apiErrors.email;
        }
        if (apiErrors.password1) {
          fieldErrors.password = Array.isArray(apiErrors.password1)
            ? apiErrors.password1[0]
            : apiErrors.password1;
        }
        if (apiErrors.password2) {
          fieldErrors.password2 = Array.isArray(apiErrors.password2)
            ? apiErrors.password2[0]
            : apiErrors.password2;
        }
        if (apiErrors.non_field_errors) {
          fieldErrors.form = Array.isArray(
            apiErrors.non_field_errors,
          )
            ? apiErrors.non_field_errors[0]
            : apiErrors.non_field_errors;
        }

        setErrors(fieldErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: "",
      password: "",
      password2: "",
    });
    setErrors({});
    setMode("login");
    onClose();
  };

  const switchMode = () => {
    setMode((prev) => (prev === "login" ? "register" : "login"));
    setErrors({});
  };

  if (!isOpen) return null;

  // Show password reset form
  if (isReset) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center 
          justify-center bg-black bg-opacity-50 p-4"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <div
          className="bg-surface rounded-lg shadow-xl w-full 
            max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-divider p-6 pb-4">
            <div className="flex items-center justify-between">
              <h2
                id="auth-modal-title"
                className="text-2xl font-bold text-primary-content"
              >
                Reset Password
              </h2>
              <button
                onClick={handleClose}
                className="text-secondary-content hover:text-primary-content
                  transition-colors p-1 rounded-md hover:bg-hover"
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <PasswordResetRequest
            onBack={() => setMode("login")}
            onClose={handleClose}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center 
        justify-center bg-black bg-opacity-50 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full 
          max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-divider p-6 pb-4">
          <div className="flex items-center justify-between">
            <h2
              id="auth-modal-title"
              className="text-2xl font-bold text-primary-content"
            >
              {isLogin ? "Login" : "Create Account"}
            </h2>
            <button
              onClick={handleClose}
              className="text-secondary-content hover:text-primary-content
                transition-colors p-1 rounded-md hover:bg-hover"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setMode("login")}
              className={`pb-2 px-1 font-medium border-b-2 
                transition-colors ${
                  isLogin
                    ? "border-primary text-primary-content"
                    : "border-transparent text-secondary-content hover:text-primary-content"
                }`}
            >
              Login
            </button>
            <button
              onClick={() => setMode("register")}
              className={`pb-2 px-1 font-medium border-b-2 
                transition-colors ${
                  !isLogin
                    ? "border-primary text-primary-content"
                    : "border-transparent text-secondary-content hover:text-primary-content"
                }`}
            >
              Register
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.form && (
            <div
              className="bg-negative bg-opacity-10 border 
              border-negative text-negative p-3 rounded-md text-sm"
            >
              {errors.form}
            </div>
          )}

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium 
                text-primary-content mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-page border rounded-md 
                text-primary-content placeholder-secondary-content
                focus:outline-none focus:ring-2 focus:ring-primary 
                ${errors.email ? "border-negative" : "border-divider"}`}
              placeholder="Enter your email"
              autoComplete="email"
              autoFocus
            />
            {errors.email && (
              <p className="mt-1 text-sm text-negative">
                {errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-primary-content"
              >
                Password
              </label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setMode("reset")}
                  className="text-xs text-primary hover:underline"
                >
                  Forgot Password?
                </button>
              )}
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-page border rounded-md 
                text-primary-content placeholder-secondary-content
                focus:outline-none focus:ring-2 focus:ring-primary 
                ${errors.password ? "border-negative" : "border-divider"}`}
              placeholder={
                isLogin ? "Enter your password" : "Min 8 characters"
              }
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-negative">
                {errors.password}
              </p>
            )}
          </div>

          {/* Confirm Password (register only) */}
          {!isLogin && (
            <div>
              <label
                htmlFor="password2"
                className="block text-sm font-medium 
                  text-primary-content mb-1"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="password2"
                name="password2"
                value={formData.password2}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-page border rounded-md 
                  text-primary-content placeholder-secondary-content
                  focus:outline-none focus:ring-2 focus:ring-primary 
                  ${errors.password2 ? "border-negative" : "border-divider"}`}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
              {errors.password2 && (
                <p className="mt-1 text-sm text-negative">
                  {errors.password2}
                </p>
              )}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary text-white py-2 px-4 
              rounded-md hover:bg-primary-hover focus:outline-none 
              focus:ring-2 focus:ring-primary focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors font-medium"
          >
            {isLoading
              ? "Please wait..."
              : isLogin
                ? "Login"
                : "Create Account"}
          </button>

          {/* Switch mode link */}
          <p className="text-center text-sm text-secondary-content">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={switchMode}
              className="text-primary hover:underline font-medium"
            >
              {isLogin ? "Register" : "Login"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
